"use client";

import { useEffect } from "react";
import { AssistantType } from "@speakai/shared";
import { useAuth } from "@/hooks/use-auth";
import { usePersonalizationContext } from "@/hooks/use-personalization-context";
import { getPersonaBucket, PERSONA_ASSISTANT_TYPE } from "@/features/onboarding/persona";
import { useUpdateProfile } from "@/features/profile/mutations";

/**
 * Users already seeded in this page session, keyed by user id.
 *
 * Module scope rather than a ref because the mount this runs on is torn down
 * whenever the user leaves the dashboard route tree and returns, and a fresh ref
 * would seed again. `useAuth().user` is provider state that the mutation cannot
 * refresh, so the stored-value guard alone cannot cover that gap.
 */
const seededUserIds = new Set<string>();

/**
 * Persists the user's computed persona assistant to `preferences.assistantType`
 * once per session, so server-side chat paths that have no UI to derive it from
 * (automations, MCP/API, Slack) can route to the right assistant.
 *
 * Writes at most once, never overwrites an existing stored value, and never
 * stores GENERAL — GENERAL is the server's own fallback, so persisting it would
 * be indistinguishable from "not set" while permanently blocking a later, better
 * value. Mount app-wide: users who only ever trigger automations never open the
 * chat dialog, and they are the cohort this exists to serve.
 *
 * Deliberately skips users who have not finished onboarding. The stored value is
 * written once and never overwritten, so seeding before the survey would lock in
 * a persona derived from Apollo signals alone and the goal the user is about to
 * pick could never correct it.
 *
 * Does not refresh the auth user afterwards: that path clears the session and
 * hard-redirects to login when the profile fetch fails, which is far worse than
 * the duplicate idempotent write it would prevent.
 */
export function useSeedAssistantPreference(): void {
  const { user, isLoading: isAuthLoading, isLoggedIn } = useAuth();
  const personalizationContext = usePersonalizationContext();
  const { mutate: updateProfile } = useUpdateProfile();

  const userId = user?._id;
  const storedAssistantType = user?.preferences?.assistantType;
  const needsOnboarding = Boolean(user?.isNewUser && !user.onboarding?.goal);
  const defaultAssistantType = PERSONA_ASSISTANT_TYPE[getPersonaBucket(personalizationContext)];

  useEffect(() => {
    if (
      isAuthLoading ||
      !isLoggedIn ||
      !userId ||
      needsOnboarding ||
      seededUserIds.has(userId) ||
      storedAssistantType ||
      defaultAssistantType === AssistantType.GENERAL
    ) {
      return;
    }
    seededUserIds.add(userId);
    updateProfile(
      { preferences: { assistantType: defaultAssistantType } },
      { onError: () => seededUserIds.delete(userId) },
    );
  }, [
    isAuthLoading,
    isLoggedIn,
    userId,
    needsOnboarding,
    storedAssistantType,
    defaultAssistantType,
    updateProfile,
  ]);
}

/** Test seam: clears the module-level session latch between cases. */
export function __resetSeededUsersForTest(): void {
  seededUserIds.clear();
}
